import type { SingleValue } from "react-select";

import PeriodRangeFilter from "@/components/PeriodRangeFilter";
import { SelectInput } from "components/Select";
import { Input } from "components/ui/input";

import {
  CONTROL_NUMBER_MAX_LENGTH,
  CUSTOMER_DOCUMENT_MAX_LENGTH,
  EXCHANGE_STATUS_OPTIONS,
  FilterOption,
  OPERATOR_MAX_LENGTH,
  STORE_EXCHANGE_LABELS,
} from "../types";

interface StoreExchangeFiltersProps {
  readonly storeOptions: readonly FilterOption[];
  readonly storeCode: number | undefined;
  readonly isStoreLocked: boolean;
  readonly onStoreChange: (storeCode: number | undefined) => void;
  readonly startDate: string | undefined;
  readonly endDate: string | undefined;
  readonly onPeriodChange: (
    startDate: string | undefined,
    endDate: string | undefined,
  ) => void;
  readonly status: string | undefined;
  readonly controlNumber: string;
  readonly customerDocument: string;
  readonly operator: string;
  readonly onStatusChange: (value: string | undefined) => void;
  readonly onControlNumberChange: (value: string) => void;
  readonly onCustomerDocumentChange: (value: string) => void;
  readonly onOperatorChange: (value: string) => void;
}

const findOption = (
  options: readonly FilterOption[],
  value: string | undefined,
): FilterOption | null =>
  options.find((option: FilterOption) => option.value === value) ?? null;

const readOptionValue = (newValue: unknown): string | undefined =>
  (newValue as SingleValue<FilterOption>)?.value ?? undefined;

const fieldLabel = "text-xs text-slate-500 mb-1 block";

function RequiredMark() {
  return <span className="text-red-500"> *</span>;
}
const fieldWidth = "w-56";

export function StoreExchangeFilters(props: StoreExchangeFiltersProps) {
  const currentStore: FilterOption | null = findOption(
    props.storeOptions,
    props.storeCode === undefined ? undefined : String(props.storeCode),
  );

  const handleStoreChange = (newValue: unknown): void => {
    const value: string | undefined = readOptionValue(newValue);
    const parsed: number = Number(value);
    props.onStoreChange(
      value !== undefined && !Number.isNaN(parsed) ? parsed : undefined,
    );
  };

  return (
    <div className="flex flex-wrap items-end gap-4 mt-4">
      <div className={fieldWidth}>
        <label className={fieldLabel}>
          {STORE_EXCHANGE_LABELS.storeLabel}
          <RequiredMark />
        </label>
        <SelectInput
          options={props.storeOptions}
          value={currentStore}
          onChange={handleStoreChange}
          isClearable={!props.isStoreLocked}
          isDisabled={props.isStoreLocked}
          placeholder={STORE_EXCHANGE_LABELS.storePlaceholder}
        />
      </div>

      <div className="flex flex-col">
        <label className={fieldLabel}>
          {STORE_EXCHANGE_LABELS.periodLabel}
          <RequiredMark />
        </label>
        <PeriodRangeFilter
          start={props.startDate}
          end={props.endDate}
          onChange={props.onPeriodChange}
          label={STORE_EXCHANGE_LABELS.periodPlaceholder}
          inline
        />
      </div>

      <div className={fieldWidth}>
        <label className={fieldLabel}>
          {STORE_EXCHANGE_LABELS.statusLabel}
        </label>
        <SelectInput
          options={EXCHANGE_STATUS_OPTIONS}
          value={findOption(EXCHANGE_STATUS_OPTIONS, props.status)}
          onChange={(newValue: unknown) =>
            props.onStatusChange(readOptionValue(newValue))
          }
          isClearable
          placeholder={STORE_EXCHANGE_LABELS.allOption}
        />
      </div>

      <div className="w-40">
        <label className={fieldLabel}>
          {STORE_EXCHANGE_LABELS.controlNumberLabel}
        </label>
        <Input
          value={props.controlNumber}
          onChange={event => props.onControlNumberChange(event.target.value)}
          placeholder={STORE_EXCHANGE_LABELS.controlNumberPlaceholder}
          maxLength={CONTROL_NUMBER_MAX_LENGTH}
          inputMode="numeric"
          className="h-8"
        />
      </div>

      <div className="w-48">
        <label className={fieldLabel}>
          {STORE_EXCHANGE_LABELS.customerDocumentLabel}
        </label>
        <Input
          value={props.customerDocument}
          onChange={event => props.onCustomerDocumentChange(event.target.value)}
          placeholder={STORE_EXCHANGE_LABELS.customerDocumentPlaceholder}
          maxLength={CUSTOMER_DOCUMENT_MAX_LENGTH}
          className="h-8"
        />
      </div>

      <div className="w-40">
        <label className={fieldLabel}>
          {STORE_EXCHANGE_LABELS.operatorLabel}
        </label>
        <Input
          value={props.operator}
          onChange={event => props.onOperatorChange(event.target.value)}
          placeholder={STORE_EXCHANGE_LABELS.operatorPlaceholder}
          maxLength={OPERATOR_MAX_LENGTH}
          className="h-8"
        />
      </div>
    </div>
  );
}
